import React, { useState, useEffect, useContext, useMemo } from 'react';
import AppContext from 'context/Context';
import { Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHourglassHalf,
    faClock,
    faSave,
    faSpinner,
    faShieldAlt,
    faTriangleExclamation,
    faLock,
    faCheckCircle,
    faRobot,
    faCommentDots,
    faUndo,
    faInfoCircle,
    faBell
} from '@fortawesome/free-solid-svg-icons';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
import { validateInappropriateLanguage } from '../../utils/profanitySanitizer';

const InactivityRulesSettingsSection = () => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { token, idCatTipoUsuario } = useToken();
    const isAdmin = Number(idCatTipoUsuario) === 1;
    const { showSuccessToast, showErrorToast } = useToast();

    const [rules, setRules] = useState([]);
    const [originalRules, setOriginalRules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);

    // Cargar las reglas desde el backend (Nivel 1 por defecto)
    const fetchRules = async () => {
        setIsLoading(true);
        try {
            const resp = await fetch('/api/SupportEngine/GetInactivityRules?supportLevelId=1', {
                headers: {
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                }
            });

            if (resp.ok) {
                const res = await resp.json();
                const data = res?.data || (Array.isArray(res) ? res : []);
                if (Array.isArray(data)) {
                    const sorted = [...data].sort((a, b) => (a.iOrden || 0) - (b.iOrden || 0));
                    const normalized = sorted.map((r, idx) => {
                        const isClosure = r.vTipoAccion === 'CIERRE';
                        const isFirstAviso = !isClosure && idx === 0;
                        if (isClosure || isFirstAviso) {
                            return { ...r, bIsActive: true };
                        }
                        return r;
                    });
                    setRules(normalized);
                    setOriginalRules(JSON.parse(JSON.stringify(normalized)));
                }
            } else {
                if (showErrorToast) showErrorToast('Error', 'No se pudieron cargar las reglas de inactividad.');
            }
        } catch (err) {
            console.error('Error al cargar reglas de inactividad:', err);
            if (showErrorToast) showErrorToast('Error de conexión al obtener las reglas.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAdmin || !token) return;
        fetchRules();
    }, [token, isAdmin]);

    // Separar reglas: Múltiples avisos preventivos y exactamente un cierre
    const avisoRules = useMemo(() => {
        return rules.filter(r => r.vTipoAccion !== 'CIERRE');
    }, [rules]);

    const cierreRule = useMemo(() => {
        return rules.find(r => r.vTipoAccion === 'CIERRE');
    }, [rules]);

    // Cambiar estado activo/inactivo y guardar de inmediato
    const handleToggleActive = async (rule, newActiveState) => {
        if (!rule) return;
        const id = rule.idConfiguracionInactividadMensaje;
        if (savingId === id) return;

        if (!newActiveState) {
            const a1 = avisoRules[0];
            if (a1 && id === a1.idConfiguracionInactividadMensaje) {
                if (showErrorToast) {
                    showErrorToast('Acción no permitida', 'El Aviso 1 es obligatorio y no puede desactivarse. Si deseas un solo aviso, desactiva el Aviso 2.');
                }
                return;
            }
            if (cierreRule && id === cierreRule.idConfiguracionInactividadMensaje) {
                if (showErrorToast) {
                    showErrorToast('Acción no permitida', 'La regla de Cierre es obligatoria y no puede desactivarse.');
                }
                return;
            }
        }

        setSavingId(id);
        try {
            const resp = await fetch('/api/SupportEngine/SaveInactivityRule', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                },
                body: JSON.stringify({
                    idConfiguracionInactividadMensaje: id,
                    iMinutosInactividad: Number(rule.iMinutosInactividad || 1),
                    vTipoAccion: rule.vTipoAccion || 'AVISO',
                    bIsActive: newActiveState,
                    vMensaje: rule.vMensaje || ''
                })
            });

            if (resp.ok) {
                const savedRule = { ...rule, bIsActive: newActiveState };
                setRules(prev => prev.map(r => (r.idConfiguracionInactividadMensaje === id ? savedRule : r)));
                setOriginalRules(prev => prev.map(r => (r.idConfiguracionInactividadMensaje === id ? JSON.parse(JSON.stringify(savedRule)) : r)));
            } else {
                const errData = await resp.json().catch(() => ({}));
                if (showErrorToast) {
                    showErrorToast('Error', errData.message || 'No se pudo actualizar el estado de la regla.');
                }
            }
        } catch (err) {
            console.error('Error al cambiar estado de regla:', err);
            if (showErrorToast) showErrorToast('Error de conexión', err.message);
        } finally {
            setSavingId(null);
        }
    };

    // Manejar cambios en campos de una regla
    const handleFieldChange = (id, field, value) => {
        if (field === 'bIsActive') {
            const a1 = avisoRules[0];
            if (a1 && id === a1.idConfiguracionInactividadMensaje && !value) {
                if (showErrorToast) {
                    showErrorToast('Acción no permitida', 'El Aviso 1 es obligatorio y no puede desactivarse. Si deseas un solo aviso, desactiva el Aviso 2.');
                }
                return;
            }
            if (cierreRule && id === cierreRule.idConfiguracionInactividadMensaje && !value) {
                if (showErrorToast) {
                    showErrorToast('Acción no permitida', 'La regla de Cierre es obligatoria y no puede desactivarse.');
                }
                return;
            }
        }

        setRules(prev =>
            prev.map(r => {
                if (r.idConfiguracionInactividadMensaje === id) {
                    return { ...r, [field]: value };
                }
                return r;
            })
        );
    };

    // Validación de tiempos y secuencia entre reglas
    const getRuleTimeValidation = ruleToCheck => {
        if (!ruleToCheck) return { isValid: true };
        const id = ruleToCheck.idConfiguracionInactividadMensaje;
        const mins = Number(ruleToCheck.iMinutosInactividad);

        if (!mins || mins < 1) {
            return { isValid: false, message: 'Los minutos de inactividad deben ser al menos 1.' };
        }

        const a1 = avisoRules[0];
        const a2 = avisoRules[1];
        const closure = cierreRule;

        const m1 = a1?.idConfiguracionInactividadMensaje === id ? mins : Number(a1?.iMinutosInactividad || 0);
        const m2 = a2?.idConfiguracionInactividadMensaje === id ? mins : Number(a2?.iMinutosInactividad || 0);
        const mc = closure?.idConfiguracionInactividadMensaje === id ? mins : Number(closure?.iMinutosInactividad || 0);

        const isAviso2Active = a2?.idConfiguracionInactividadMensaje === id
            ? Boolean(ruleToCheck.bIsActive)
            : Boolean(a2?.bIsActive);

        // 1. Validación para Aviso 1
        if (a1 && id === a1.idConfiguracionInactividadMensaje) {
            if (a2 && isAviso2Active && mins >= m2) {
                return {
                    isValid: false,
                    message: `El tiempo del Aviso 1 (${mins} min) no puede ser mayor o igual al del Aviso 2 (${m2} min).`
                };
            }
            if (closure && mins >= mc) {
                return {
                    isValid: false,
                    message: `El tiempo del Aviso 1 (${mins} min) no puede ser mayor o igual al tiempo de Cierre (${mc} min).`
                };
            }
        }

        // 2. Validación para Aviso 2
        if (a2 && id === a2.idConfiguracionInactividadMensaje) {
            if (!isAviso2Active) {
                return { isValid: true };
            }
            if (a1 && mins <= m1) {
                return {
                    isValid: false,
                    message: `El tiempo del Aviso 2 (${mins} min) no puede ser menor o igual al del Aviso 1 (${m1} min).`
                };
            }
            if (closure && mins >= mc) {
                return {
                    isValid: false,
                    message: `El tiempo del Aviso 2 (${mins} min) no puede ser mayor o igual al tiempo de Cierre (${mc} min).`
                };
            }
        }

        // 3. Validación para Cierre
        if (closure && id === closure.idConfiguracionInactividadMensaje) {
            if (a2 && isAviso2Active) {
                if (mins <= m2) {
                    return {
                        isValid: false,
                        message: `El tiempo de Cierre (${mins} min) no puede ser menor o igual al del Aviso 2 (${m2} min), ya que está activo.`
                    };
                }
            } else if (a1) {
                if (mins <= m1) {
                    return {
                        isValid: false,
                        message: `El tiempo de Cierre (${mins} min) no puede ser menor o igual al del Aviso 1 (${m1} min).`
                    };
                }
            }
        }

        return { isValid: true };
    };

    // Descartar cambios de una regla puntual
    const handleResetRule = id => {
        const orig = originalRules.find(r => r.idConfiguracionInactividadMensaje === id);
        if (orig) {
            setRules(prev =>
                prev.map(r => (r.idConfiguracionInactividadMensaje === id ? JSON.parse(JSON.stringify(orig)) : r))
            );
        }
    };

    // Determinar si una regla tiene cambios sin guardar
    const isRuleDirty = rule => {
        if (!rule) return false;
        const orig = originalRules.find(r => r.idConfiguracionInactividadMensaje === rule.idConfiguracionInactividadMensaje);
        if (!orig) return false;
        return (
            Number(rule.iMinutosInactividad) !== Number(orig.iMinutosInactividad) ||
            Boolean(rule.bIsActive) !== Boolean(orig.bIsActive) ||
            (rule.vMensaje || '') !== (orig.vMensaje || '')
        );
    };

    // Guardar una regla puntual
    const handleSaveRule = async rule => {
        if (!rule) return;
        const id = rule.idConfiguracionInactividadMensaje;
        const mins = Number(rule.iMinutosInactividad);

        if (!mins || mins < 1) {
            if (showErrorToast) showErrorToast('Error de validación', 'Los minutos de inactividad deben ser al menos 1.');
            return;
        }

        const isClosure = rule.vTipoAccion === 'CIERRE' || rule === cierreRule;
        const a1 = avisoRules[0];
        const isMandatory = isClosure || (a1 && id === a1.idConfiguracionInactividadMensaje);
        const activeToSave = isMandatory ? true : Boolean(rule.bIsActive);

        // Validación de secuencia de tiempos
        const timeVal = getRuleTimeValidation(rule);
        if (!timeVal.isValid) {
            if (showErrorToast) {
                showErrorToast('Validación de tiempos', timeVal.message);
            }
            return;
        }

        // Validación de palabras inapropiadas en el mensaje
        if (rule.vMensaje && typeof validateInappropriateLanguage === 'function') {
            const profanity = validateInappropriateLanguage(rule.vMensaje);
            if (!profanity.isValid) {
                if (showErrorToast) {
                    showErrorToast('Lenguaje no permitido', `El mensaje contiene la palabra no permitida: "${profanity.foundWord}"`);
                }
                return;
            }
        }

        setSavingId(id);
        try {
            const resp = await fetch('/api/SupportEngine/SaveInactivityRule', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...(token ? { 'Authorization': 'Bearer ' + token } : {})
                },
                body: JSON.stringify({
                    idConfiguracionInactividadMensaje: id,
                    iMinutosInactividad: mins,
                    vTipoAccion: rule.vTipoAccion || (isClosure ? 'CIERRE' : 'AVISO'),
                    bIsActive: activeToSave,
                    vMensaje: rule.vMensaje || ''
                })
            });

            if (resp.ok) {
                const savedRule = { ...rule, bIsActive: activeToSave };

                if (showSuccessToast) {
                    const label = isClosure
                        ? 'Cierre automático'
                        : `Aviso #${rule.iOrden || id}`;
                    showSuccessToast('Regla guardada', `${label} actualizado exitosamente.`);
                }
                setRules(prev =>
                    prev.map(r => (r.idConfiguracionInactividadMensaje === id ? savedRule : r))
                );
                setOriginalRules(prev =>
                    prev.map(r => (r.idConfiguracionInactividadMensaje === id ? JSON.parse(JSON.stringify(savedRule)) : r))
                );
            } else {
                const errData = await resp.json().catch(() => ({}));
                if (showErrorToast) {
                    showErrorToast('Error', errData.message || 'No se pudo guardar la regla de inactividad.');
                }
            }
        } catch (err) {
            console.error('Error al guardar regla:', err);
            if (showErrorToast) showErrorToast('Error de conexión', err.message);
        } finally {
            setSavingId(null);
        }
    };

    // Renderizar tarjeta individual de regla
    const renderRuleCard = (rule, isClosure = false, stepNumber = 1) => {
        if (!rule) return null;
        const id = rule.idConfiguracionInactividadMensaje;
        const dirty = isRuleDirty(rule);
        const isSaving = savingId === id;
        const isMandatory = (!isClosure && stepNumber === 1) || isClosure;
        const isActive = isMandatory ? true : Boolean(rule.bIsActive);

        const timeVal = getRuleTimeValidation(rule);
        const hasTimeError = isActive && !timeVal.isValid;

        const a1 = avisoRules[0];
        const a2 = avisoRules[1];
        const closure = cierreRule;

        const m1 = Number(a1?.iMinutosInactividad || 0);
        const m2 = Number(a2?.iMinutosInactividad || 0);
        const mc = Number(closure?.iMinutosInactividad || 0);
        const isAviso2Active = Boolean(a2?.bIsActive);

        return (
            <Col
                key={id}
                md={isClosure ? 12 : 6}
                xs={12}
            >
                <div
                    className="card rounded-3 shadow-sm overflow-hidden h-100"
                    style={{
                        backgroundColor: isDark ? '#121e2d' : '#f6f8ffff',
                        border: isDark
                            ? (hasTimeError ? '1px solid #ef4444' : (dirty ? '1px solid #3b82f6' : '1px solid #233142'))
                            : (hasTimeError ? '1px solid #ef4444' : (dirty ? '1px solid #93c5fd' : '1px solid #e2e8f0')),
                        opacity: isActive ? 1 : 0.75
                    }}
                >
                    {/* Cabecera de la Tarjeta */}
                    <div
                        className="p-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 border-bottom"
                        style={{
                            backgroundColor: isDark ? '#182635' : '#f8fafc',
                            borderColor: isDark ? '#233142' : '#e2e8f0'
                        }}
                    >
                        <div className="d-flex align-items-center gap-2.5">
                            <div>
                                <span className="fw-bold me-2" style={{ color: isDark ? '#f9fafd' : '#0f172a', fontSize: '0.92rem' }}>
                                    {isClosure ? 'Cierre Automático' : `Aviso Preventivo #${stepNumber}`}
                                </span>
                                <span
                                    className="badge px-2 py-0.5"
                                    style={{
                                        fontSize: '0.70rem',
                                        backgroundColor: isClosure
                                            ? (isDark ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2')
                                            : (isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7'),
                                        color: isClosure
                                            ? (isDark ? '#f87171' : '#b91c1c')
                                            : (isDark ? '#fbbf24' : '#b45309'),
                                        border: isClosure
                                            ? '1px solid rgba(239, 68, 68, 0.3)'
                                            : '1px solid rgba(245, 158, 11, 0.3)'
                                    }}
                                >
                                    {isClosure ? 'CIERRE DEFINITIVO' : 'AVISO PREVENTIVO'}
                                </span>
                            </div>
                        </div>

                        {/* Switch de Activo / Inactivo */}
                        <div className="d-flex align-items-center gap-3">
                            <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
                                <input
                                    className="form-check-input shadow-none"
                                    type="checkbox"
                                    role="switch"
                                    id={`switch-${id}`}
                                    checked={isActive}
                                    disabled={isMandatory || isSaving}
                                    onChange={e => handleToggleActive(rule, e.target.checked)}
                                    style={{ cursor: isMandatory || isSaving ? 'not-allowed' : 'pointer' }}
                                />
                                <label
                                    className="form-check-label small fw-semibold mb-0"
                                    htmlFor={`switch-${id}`}
                                    style={{
                                        color: isActive ? '#10b981' : (isDark ? '#94a3b8' : '#64748b'),
                                        cursor: isMandatory ? 'default' : 'pointer'
                                    }}
                                    title={isMandatory ? (isClosure ? 'El cierre es obligatorio' : 'El Aviso 1 es obligatorio. Para usar solo uno, desactiva el Aviso 2.') : undefined}
                                >
                                    {isMandatory ? 'Activo (Obligatorio)' : (isActive ? 'Activo' : 'Inactivo')}
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Cuerpo de la Tarjeta */}
                    <div className="card-body p-4" style={{ pointerEvents: isActive ? 'auto' : 'none', opacity: isActive ? 1 : 0.65 }}>
                        <div className="row g-3 align-items-start">
                            {/* Minutos de Inactividad */}
                            <div className="col-12 col-md-4 col-lg-3">
                                <label className="form-label small fw-bold mb-1" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                                    <FontAwesomeIcon icon={faClock} className="me-1 text-primary" />
                                    {isClosure ? 'Tiempo para auto-cierre' : 'Tiempo para enviar aviso'}
                                </label>
                                <div className="input-group input-group-sm">
                                    <input
                                        type="number"
                                        min="1"
                                        max="1440"
                                        disabled={!isActive}
                                        className={`form-control pe-0 shadow-none fw-semibold ${hasTimeError ? 'is-invalid border-danger' : ''}`}
                                        value={rule.iMinutosInactividad || ''}
                                        onChange={e => handleFieldChange(id, 'iMinutosInactividad', Number(e.target.value))}
                                        style={{
                                            backgroundColor: !isActive ? (isDark ? '#162232' : '#f1f5f9') : (isDark ? '#1e293b' : '#ffffff'),
                                            borderColor: hasTimeError ? '#ef4444' : (isDark ? '#334155' : '#cbd5e1'),
                                            color: !isActive ? (isDark ? '#64748b' : '#94a3b8') : (isDark ? '#f8fafc' : '#0f172a'),
                                            cursor: !isActive ? 'not-allowed' : 'text'
                                        }}
                                    />
                                    <span
                                        className="input-group-text"
                                        style={{
                                            backgroundColor: isDark ? '#182635' : '#f1f5f9',
                                            borderColor: hasTimeError ? '#ef4444' : (isDark ? '#334155' : '#cbd5e1'),
                                            color: isDark ? '#94a3b8' : '#64748b'
                                        }}
                                    >
                                        minutos
                                    </span>
                                </div>
                                {hasTimeError ? (
                                    <div className="text-danger small mt-1 fw-semibold" style={{ fontSize: '0.74rem' }}>
                                        <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                                        {timeVal.message}
                                    </div>
                                ) : (
                                    <div className="form-text small" style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: '0.74rem' }}>
                                        {isClosure
                                            ? (isAviso2Active
                                                ? `Minutos sin respuesta para cerrar (debe ser mayor al Aviso 2: > ${m2} min).`
                                                : `Minutos sin respuesta para cerrar (Aviso 2 inactivo, debe ser mayor al Aviso 1: > ${m1} min).`)
                                            : stepNumber === 1
                                                ? (isAviso2Active
                                                    ? `Minutos de inactividad antes de detonar este aviso (debe ser menor al Aviso 2: < ${m2} min).`
                                                    : `Minutos de inactividad antes de detonar este aviso (debe ser menor al Cierre: < ${mc} min).`)
                                                : `Minutos de inactividad antes de detonar este aviso (debe ser mayor al Aviso 1: > ${m1} min y menor al Cierre: < ${mc} min).`}
                                    </div>
                                )}
                            </div>

                            {/* Redactor del Mensaje en el mismo Row */}
                            <div className="col-12 col-md-8 col-lg-9">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <label className="form-label small fw-bold mb-0" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                                        <FontAwesomeIcon icon={faCommentDots} className="me-1 text-primary" />
                                        Mensaje enviado automáticamente por WhatsApp
                                    </label>
                                    <span className="small text-muted" style={{ fontSize: '0.72rem' }}>
                                        {rule.vMensaje ? rule.vMensaje.length : 0} caracteres
                                    </span>
                                </div>
                                <textarea
                                    className="form-control shadow-none"
                                    rows={3}
                                    value={rule.vMensaje || ''}
                                    onChange={e => handleFieldChange(id, 'vMensaje', e.target.value)}
                                    disabled={!isActive}
                                    placeholder={isClosure
                                        ? 'Escribe el mensaje de despedida/cierre por inactividad...'
                                        : 'Escribe el recordatorio preventivo que enviará el bot...'}
                                    style={{
                                        fontSize: '0.86rem',
                                        backgroundColor: !isActive ? (isDark ? '#162232' : '#f1f5f9') : (isDark ? '#1e293b' : '#ffffff'),
                                        borderColor: isDark ? '#334155' : '#cbd5e1',
                                        color: !isActive ? (isDark ? '#64748b' : '#94a3b8') : (isDark ? '#f8fafc' : '#0f172a'),
                                        cursor: !isActive ? 'not-allowed' : 'text'
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Pie de la tarjeta */}
                    <div
                        className="p-3 d-flex align-items-center justify-content-between border-top"
                        style={{
                            backgroundColor: isDark ? '#14202e' : '#fafafa',
                            borderColor: isDark ? '#233142' : '#e2e8f0'
                        }}
                    >
                        <div className="small">
                            {!isActive ? (
                                <span className="badge bg-secondary-subtle text-secondary px-2 py-1" style={{ fontSize: '0.74rem' }}>
                                    <FontAwesomeIcon icon={faLock} className="me-1" />
                                    Regla desactivada
                                </span>
                            ) : hasTimeError ? (
                                <span className="badge bg-danger px-2 py-1">
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                                    Tiempo inválido
                                </span>
                            ) : dirty ? (
                                <span className="badge bg-warning text-dark px-2 py-1">
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                                    Cambios sin guardar
                                </span>
                            ) : (
                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
                                    Configuración guardada
                                </span>
                            )}
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            {dirty && isActive && (
                                <button
                                    type="button"
                                    onClick={() => handleResetRule(id)}
                                    disabled={isSaving}
                                    className="btn btn-sm btn-outline-secondary shadow-none d-inline-flex align-items-center gap-1.5"
                                    style={{ fontSize: '0.78rem' }}
                                >
                                    <FontAwesomeIcon icon={faUndo} style={{ fontSize: '0.70rem' }} />
                                    Descartar
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => handleSaveRule(rule)}
                                disabled={!isActive || isSaving || !dirty || hasTimeError}
                                className={`btn btn-sm shadow-none d-inline-flex align-items-center gap-1.5 ${
                                    isClosure ? 'btn-danger' : 'btn-primary'
                                }`}
                                style={{
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                    cursor: !isActive ? 'not-allowed' : 'pointer',
                                    opacity: !isActive ? 0.5 : 1
                                }}
                            >
                                {isSaving ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.75rem' }} />
                                        Guardando...
                                    </>
                                ) : (
                                    <div className="d-flex align-items-center gap-2">
                                        <FontAwesomeIcon icon={faSave} style={{ fontSize: '0.75rem' }} />
                                        {isClosure ? 'Guardar Regla de Cierre' : 'Guardar Aviso'}
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </Col>
        );
    };

    if (!isAdmin) {
        return (
            <div className="alert alert-warning p-4 rounded-3 text-center my-4">
                <FontAwesomeIcon icon={faShieldAlt} className="fa-2x mb-2 text-warning" />
                <h6 className="fw-bold mb-1">Acceso Restringido</h6>
                <p className="mb-0 small text-muted">
                    Esta sección es exclusiva para administradores del sistema. No tienes permisos para modificar las reglas de inactividad.
                </p>
            </div>
        );
    }

    return (
        <div className="container-fluid p-0">
            {/* Cabecera Principal */}
            <div
                className="card mb-4 border-0 shadow-sm rounded-3"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                }}
            >
                <div className="card-body p-4">
                    <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                                style={{
                                    width: '46px',
                                    height: '46px',
                                    backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                                    color: isDark ? '#60a5fa' : '#2563eb'
                                }}
                            >
                                <FontAwesomeIcon icon={faHourglassHalf} style={{ fontSize: '1.25rem' }} />
                            </div>
                            <div>
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                    <h5 className="fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                                        Reglas de Inactividad
                                    </h5>
                                    <span
                                        className="badge rounded-pill px-2.5 py-1"
                                        style={{
                                            fontSize: '0.70rem',
                                            backgroundColor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff',
                                            color: isDark ? '#a5b4fc' : '#4338ca'
                                        }}
                                    >
                                        Exclusivo Administrador
                                    </span>
                                </div>
                                <p className="mb-0 small" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                    Configuración de la secuencia de avisos preventivos y cierre automático del bot para tickets sin respuesta.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Banner Informativo y Comportamiento por Nivel */}
            <div
                className="card mb-4 border-0 rounded-3"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#f8fafc',
                    border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                }}
            >
                <div className="card-body p-3 d-flex align-items-start gap-2.5">
                    <FontAwesomeIcon icon={faInfoCircle} className="mt-1 flex-shrink-0" style={{ color: '#3b82f6', fontSize: '1rem' }} />
                    <div className="small ms-2" style={{ color: isDark ? '#cbd5e1' : '#475569' }}>
                        <span className="fw-semibold"> Funcionamiento de la secuencia:</span>
                        {' '}Cuando un asesor envía el último mensaje y el cliente permanece inactivo, el bot evalúa y envía los <strong>avisos preventivos</strong> en orden ascendente (#1, #2...). Si el cliente responde o el asesor pulsa <em>"Dar más tiempo"</em>, el contador se reinicia.

                        <div className="pt-3 mt-3 border-top" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
                            <div className="fw-bold small mb-2 d-flex align-items-center gap-1.5" style={{ color: isDark ? '#f9fafd' : '#0f172a', fontSize: '0.86rem' }}>
                                <span>Comportamiento según el Nivel de Soporte del Ticket.</span>
                            </div>
                            <div className="row g-2.5">
                                <div className="col-12 col-md-6">
                                    <div
                                        className="p-3 rounded-3 h-100"
                                        style={{
                                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.08)' : '#f0fdf4',
                                            border: isDark ? '1px solid rgba(16, 185, 129, 0.25)' : '1px solid #bbf7d0'
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span
                                                className="badge rounded-pill px-2.5 py-1"
                                                style={{
                                                    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.25)' : '#dcfce7',
                                                    color: isDark ? '#34d399' : '#15803d',
                                                    fontSize: '0.74rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                Nivel 1 (Básico) o Sin Nivel
                                            </span>
                                        </div>
                                        <div className="small" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.82rem', lineHeight: 1.55 }}>
                                            <div><FontAwesomeIcon icon={faCommentDots} className="me-2 text-success" style={{ fontSize: '0.80rem' }} /><strong>Mensajes automáticos:</strong> Envía los avisos preventivos programados por WhatsApp.</div>
                                            <div className="mt-2"><FontAwesomeIcon icon={faLock} className="me-2 text-danger" style={{ fontSize: '0.80rem' }} /><strong>Cierre automático:</strong> Si el cliente no responde al agotar el tiempo límite, el bot <strong>cierra el ticket automáticamente</strong>.</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div
                                        className="p-3 rounded-3 h-100"
                                        style={{
                                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
                                            border: isDark ? '1px solid rgba(59, 130, 246, 0.25)' : '1px solid #bfdbfe'
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span
                                                className="badge rounded-pill px-2.5 py-1"
                                                style={{
                                                    backgroundColor: isDark ? 'rgba(59, 130, 246, 0.25)' : '#dbeafe',
                                                    color: isDark ? '#60a5fa' : '#1d4ed8',
                                                    fontSize: '0.74rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                Nivel 2 (Avanzado) en adelante
                                            </span>
                                        </div>
                                        <div className="small" style={{ color: isDark ? '#cbd5e1' : '#334155', fontSize: '0.82rem', lineHeight: 1.55 }}>
                                            <div className="mt-2"><FontAwesomeIcon icon={faTriangleExclamation} className="me-2 text-warning" style={{ fontSize: '0.80rem' }} /><strong>Aviso con Badge:</strong> <strong>NO se cierra el ticket</strong>; al cumplirse 15 min de inactividad, se activa la alerta visual <em>"Cliente sin responder (&gt;15 min)"</em> para seguimiento del asesor.</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contenido de Reglas */}
            {isLoading ? (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faSpinner} spin className="fa-2x text-primary mb-3" />
                    <h6 className="fw-medium text-muted">Cargando reglas de inactividad...</h6>
                </div>
            ) : rules.length === 0 ? (
                <div
                    className="card border-0 rounded-3 text-center p-5"
                    style={{
                        backgroundColor: isDark ? '#121e2d' : '#ffffff',
                        border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                    }}
                >
                    <FontAwesomeIcon icon={faRobot} className="fa-3x text-muted mb-3 opacity-50" />
                    <h5 className="fw-bold mb-1" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                        Sin reglas de inactividad configuradas
                    </h5>
                    <p className="text-muted small mb-0">
                        No se encontraron registros de reglas en la base de datos.
                    </p>
                </div>
            ) : (
                <div className="d-flex flex-column gap-4">
                    <div
                        className="card border-0 rounded-3 p-3 shadow-xs"
                        style={{
                            backgroundColor: isDark ? '#121e2d' : '#f8fafc',
                            border: isDark ? '1px solid #233142' : '1px solid #e2e8f0'
                        }}
                    >
                        <div
                            className="text-uppercase fw-bold mb-3"
                            style={{
                                fontSize: '0.68rem',
                                letterSpacing: '0.6px',
                                color: isDark ? '#94a3b8' : '#64748b'
                            }}
                        >
                            Secuencia de Inactividad
                        </div>

                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 px-1">
                            {/* 0': Inactividad Detectada */}
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        fontSize: '0.76rem',
                                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : '#e2e8f0',
                                        color: isDark ? '#94a3b8' : '#475569',
                                        border: isDark ? '1px solid rgba(148, 163, 184, 0.25)' : '1px solid #cbd5e1'
                                    }}
                                >
                                    0'
                                </div>
                                <div>
                                    <div className="fw-bold" style={{ fontSize: '0.80rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                        Inactividad Detectada
                                    </div>
                                    <div style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Última interacción del cliente
                                    </div>
                                </div>
                            </div>

                            {/* 1': Aviso Preventivo #1 */}
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        fontSize: '0.76rem',
                                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7',
                                        color: isDark ? '#fbbf24' : '#b45309',
                                        border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid #fde68a'
                                    }}
                                >
                                    {avisoRules[0]?.iMinutosInactividad ?? 1}'
                                </div>
                                <div>
                                    <div className="fw-bold" style={{ fontSize: '0.80rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                        Aviso Preventivo #1
                                    </div>
                                    <div style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Recordatorio inicial amistoso
                                    </div>
                                </div>
                            </div>

                            {/* 10': Aviso Preventivo #2 */}
                            <div className="d-flex align-items-center gap-3" style={{ opacity: avisoRules[1] && !avisoRules[1].bIsActive ? 0.6 : 1 }}>
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        fontSize: '0.76rem',
                                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.18)' : '#fef3c7',
                                        color: isDark ? '#fbbf24' : '#b45309',
                                        border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid #fde68a'
                                    }}
                                >
                                    {avisoRules[1]?.iMinutosInactividad ?? 10}'
                                </div>
                                <div>
                                    <div className="fw-bold" style={{ fontSize: '0.80rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                                        Aviso Preventivo #2
                                    </div>
                                    <div style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Alerta previa de cierre
                                    </div>
                                </div>
                            </div>

                            {/* 20': Cierre Automático */}
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        fontSize: '0.76rem',
                                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.18)' : '#fee2e2',
                                        color: isDark ? '#f87171' : '#dc2626',
                                        border: isDark ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #fecaca'
                                    }}
                                >
                                    {cierreRule?.iMinutosInactividad ?? 20}'
                                </div>
                                <div>
                                    <div className="fw-bold" style={{ fontSize: '0.80rem', color: isDark ? '#f87171' : '#dc2626' }}>
                                        Cierre Automático
                                    </div>
                                    <div style={{ fontSize: '0.70rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                        Ticket concluido por bot
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SECCIÓN 1: AVISOS PREVENTIVOS (Pueden existir varios) */}
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-2"
                                style={{
                                    width: '26px',
                                    height: '26px',
                                    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7',
                                    color: isDark ? '#fbbf24' : '#b45309'
                                }}
                            >
                                <FontAwesomeIcon icon={faBell} style={{ fontSize: '0.80rem' }} />
                            </div>
                            <h6 className="fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                                Avisos Preventivos ({avisoRules.length})
                            </h6>
                            <span className="small text-muted">
                                — Notificaciones enviadas al cliente antes del cierre
                            </span>
                        </div>

                        <Row className="g-3">
                            {avisoRules.map((rule, idx) => renderRuleCard(rule, false, idx + 1))}
                        </Row>
                    </div>

                    {/* SECCIÓN 2: CIERRE AUTOMÁTICO (Campo/Regla única de cierre) */}
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-2"
                                style={{
                                    width: '26px',
                                    height: '26px',
                                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2',
                                    color: isDark ? '#f87171' : '#dc2626'
                                }}
                            >
                                <FontAwesomeIcon icon={faLock} style={{ fontSize: '0.80rem' }} />
                            </div>
                            <h6 className="fw-bold mb-0" style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>
                                Cierre Definitivo de Ticket (Único)
                            </h6>
                            <span className="small text-muted">
                                — Acción ejecutada por el bot al agotar el tiempo (Aplica únicamente a tickets de Nivel 1 Básico o Sin Nivel)
                            </span>
                        </div>

                        <Row className="g-3">
                            {cierreRule ? (
                                renderRuleCard(cierreRule, true)
                            ) : (
                                <Col xs={12}>
                                    <div className="alert alert-secondary small rounded-3 mb-0">
                                        No se encontró una regla de tipo CIERRE configurada en la base de datos.
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InactivityRulesSettingsSection;
